import { useId, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useUpdateAdAccount } from '@/hooks/useAds'
import { formatGoogleAdsId } from '@lib/helpers/google-ads-id'
import type { AdAccountDto, AdVenueOptionDto } from '@lib/dto/ads.dto'
import { adPlaceKey, adPlaceOptions, parseAdPlaceKey } from './adPlaces'

interface AdPlaceDialogProps {
  account: AdAccountDto
  venues: AdVenueOptionDto[]
  /** The venue to start from when the account has none yet: the one a sign-in began at. */
  suggestedVenueId?: string | null
  onClose: () => void
}

/**
 * Where an account's campaigns count: a venue, or one of its tracks. Putting a
 * waiting account in use starts its first import; pointing one already in use
 * elsewhere moves its whole history with it (docs/ad-analytics.spec.md).
 */
export function AdPlaceDialog({ account, venues, suggestedVenueId = null, onClose }: AdPlaceDialogProps) {
  const id = useId()
  const update = useUpdateAdAccount()
  const options = adPlaceOptions(venues)
  const inUse = account.use === 'in_use' || account.use === 'stopped'
  const current =
    account.defaultVenueId === null ? null : adPlaceKey({ venueId: account.defaultVenueId, trackId: account.defaultTrackId })
  const suggested = options.some((o) => o.key === suggestedVenueId) ? suggestedVenueId : null
  const [key, setKey] = useState<string>(current ?? suggested ?? options[0]?.key ?? '')
  const [error, setError] = useState<string | null>(null)

  function save() {
    const place = parseAdPlaceKey(key)
    if (place === null) {
      setError('Choose a venue.')
      return
    }
    setError(null)
    update.mutate(
      { accountId: account.id, input: { action: 'use', place } },
      { onSuccess: onClose, onError: (e) => setError(requestErrorMessage(e)) },
    )
  }

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{inUse ? 'Change where it counts' : 'Put in use'}</DialogTitle>
          <DialogDescription>
            {account.name} ({formatGoogleAdsId(account.externalId)}).{' '}
            {inUse
              ? 'Its campaigns that follow the account, and all their past numbers, move to the new place.'
              : 'Its last 25 months are brought in, and it updates every night from then on.'}
          </DialogDescription>
        </DialogHeader>
        <Field label="Counts toward" htmlFor={id} error={error}>
          <Select id={id} value={key} disabled={update.isPending} onChange={(e) => setKey(e.target.value)}>
            {options.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={update.isPending || options.length === 0} onClick={save}>
            {inUse ? 'Save' : 'Put in use'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
